import { useState } from 'react'
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import type { StorefrontProduct } from '../../shared/types'
import { formatMoney } from '../../shared/money'
import { useCart } from '../cart/CartContext'
import OptionPicker from './OptionPicker'
import QuantityStepper from './QuantityStepper'
import { EggOutline } from './brand'

/**
 * `eager` — opt out of lazy loading for the cards in the first visible row. A shop with no
 * banner has its first product image as the LCP element, and an LCP image must never be
 * lazy-loaded. Eager cards deliberately keep default fetch priority so they don't compete
 * with the banner when there is one.
 */
export default function ProductCard({
  product,
  currency,
  eager = false,
}: {
  product: StorefrontProduct
  currency: string
  eager?: boolean
}) {
  const { t } = useTranslation()
  const { items, add, setQuantity } = useCart()
  const [picking, setPicking] = useState(false)
  const hasOptions = (product.optionGroups?.length ?? 0) > 0
  // Optioned products map to many variant lines; the card's stepper shows the total across them,
  // and "+" reopens the picker (add another variant) rather than incrementing one line. "-" trims
  // the most-recently-added variant line — fine-grained per-variant control is in the cart drawer.
  // ponytail: "-" always hits the last line; per-variant control lives in the cart, not the card.
  const productLines = items.filter((i) => i.product.id === product.id)
  const inCart = hasOptions
    ? productLines.reduce((n, i) => n + i.quantity, 0)
    : items.find((i) => i.key === product.id)?.quantity ?? 0

  const onAdd = () => (hasOptions ? setPicking(true) : add(product))
  const onDecrement = () => {
    if (hasOptions) {
      const last = productLines[productLines.length - 1]
      if (last) setQuantity(last.key, last.quantity - 1)
    } else {
      setQuantity(product.id, inCart - 1)
    }
  }

  return (
    <Card
      sx={{
        // Phone (xs): horizontal row — thumbnail left, content right. Tablet/PC (sm+): vertical card.
        display: 'flex',
        flexDirection: { xs: 'row', sm: 'column' },
        alignItems: { xs: 'center', sm: 'stretch' },
        gap: { xs: 1.25, sm: 0 },
        p: { xs: 1.25, sm: 0 },
        height: '100%',
      }}
    >
      {product.imageUrl ? (
        <Box
          component="img"
          src={product.imageUrl}
          alt={product.name}
          loading={eager ? undefined : 'lazy'}
          decoding="async"
          onClick={onAdd}
          sx={{
            width: { xs: 64, sm: '100%' },
            height: { xs: 64, sm: 140 },
            flexShrink: 0,
            objectFit: 'cover',
            borderRadius: { xs: '15px', sm: '16px 16px 0 0' },
            cursor: 'pointer',
          }}
        />
      ) : (
        <Box
          onClick={onAdd}
          sx={{
            width: { xs: 64, sm: '100%' },
            height: { xs: 64, sm: 140 },
            flexShrink: 0,
            borderRadius: { xs: '15px', sm: '16px 16px 0 0' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'rgba(233,166,60,0.18)',
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(233,166,60,0.12) 0 8px, transparent 8px 16px)',
          }}
        >
          <EggOutline sx={{ width: 30, height: 36 }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', p: { xs: 0, sm: 1.5 }, width: { sm: '100%' } }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {product.name}
        </Typography>
        {product.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: { xs: 'block', sm: '-webkit-box' },
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: { xs: 'nowrap', sm: 'normal' },
              textOverflow: { xs: 'ellipsis', sm: 'clip' },
            }}
          >
            {product.description}
          </Typography>
        )}
        {/* Spacer only in vertical mode, so price/Add align to the card bottom across the grid. */}
        <Box sx={{ flexGrow: { xs: 0, sm: 1 } }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: { xs: 0.75, sm: 1 } }}>
          {/* Priceless shop (D-069): no price, but keep the Add control right-aligned via the empty Box. */}
          {product.price != null ? (
            <Typography variant="subtitle1" fontWeight={700} color="primary.main">
              {formatMoney(product.price, currency)}
            </Typography>
          ) : (
            <Box />
          )}
          {inCart === 0 ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={onAdd}
              sx={{ px: 1.5, '& .MuiButton-startIcon': { mr: 0.5 }, fontSize: '0.8125rem', flexShrink: 0 }}
            >
              {t('common.add')}
            </Button>
          ) : (
            <QuantityStepper
              value={inCart}
              onDecrement={onDecrement}
              onIncrement={() => (hasOptions ? setPicking(true) : setQuantity(product.id, inCart + 1))}
            />
          )}
        </Stack>
      </Box>

      {picking && (
        <OptionPicker
          product={product}
          onClose={() => setPicking(false)}
          onAdd={(selections) => {
            add(product, 1, selections)
            setPicking(false)
          }}
        />
      )}
    </Card>
  )
}
